import type {
  InboxCaseBatchWorkflowItem,
  InboxCaseWorkflowItemStep,
  InboxCaseWorkflowStep,
} from "../../../../../../../../../../../../../api/kehrblatt/inbox-cases"

type WorkflowStepLike = Pick<InboxCaseWorkflowStep, "step_key" | "requirements_resolved_by_step">

const objectValue = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

export const workflowItemStepFor = (item: InboxCaseBatchWorkflowItem, stepKey: string) =>
  item.steps.find((entry) => entry.step_key === stepKey) ?? null

export const workflowItemOutputFor = (
  item: InboxCaseBatchWorkflowItem,
  stepKey: string,
  nestedOutputKey?: string,
) => {
  const output = objectValue(workflowItemStepFor(item, stepKey)?.output)

  return nestedOutputKey ? objectValue(output[nestedOutputKey]) : output
}

export const workflowStepIsOpen = (step: Pick<InboxCaseWorkflowItemStep, "status"> | null) =>
  Boolean(step && step.status !== "resolved" && step.status !== "skipped")

export const workflowItemHasStep = (item: InboxCaseBatchWorkflowItem, stepKey: string) =>
  Boolean(workflowItemStepFor(item, stepKey))

const stepRequirements = (step: WorkflowStepLike) =>
  step.requirements_resolved_by_step.filter((requirement) => requirement.trim())

const workflowItemHasRequirement = (item: InboxCaseBatchWorkflowItem, requirement: string) =>
  item.action_case.requirements.includes(requirement)

const workflowItemRequirementIsPending = (item: InboxCaseBatchWorkflowItem, requirement: string) =>
  item.unresolved_requirements.includes(requirement) ||
  (workflowItemHasRequirement(item, requirement) &&
    !item.action_case.resolved_requirements.includes(requirement))

export const workflowItemHasRelevantStep = (
  item: InboxCaseBatchWorkflowItem,
  step: WorkflowStepLike,
) =>
  workflowItemHasStep(item, step.step_key) ||
  stepRequirements(step).some((requirement) => workflowItemHasRequirement(item, requirement))

export const workflowItemNeedsStepFor = (
  item: InboxCaseBatchWorkflowItem,
  step: WorkflowStepLike,
) => {
  const projectedStep = workflowItemStepFor(item, step.step_key)

  if (projectedStep) return workflowStepIsOpen(projectedStep)

  return stepRequirements(step).some((requirement) =>
    workflowItemRequirementIsPending(item, requirement),
  )
}

export const workflowItemStepResolvedFor = (
  item: InboxCaseBatchWorkflowItem,
  step: WorkflowStepLike,
) => {
  const projectedStep = workflowItemStepFor(item, step.step_key)

  if (projectedStep?.status === "resolved") return true

  if (projectedStep?.status === "skipped") return false

  const relevantRequirements = stepRequirements(step).filter((requirement) =>
    workflowItemHasRequirement(item, requirement),
  )

  return (
    Boolean(relevantRequirements.length) &&
    relevantRequirements.every(
      (requirement) =>
        item.action_case.resolved_requirements.includes(requirement) &&
        !workflowItemRequirementIsPending(item, requirement),
    )
  )
}
